import { useEffect } from 'react';
import {
    filtersCurrenciesSelector,
    newFiltersPeriodByTariffSelector,
    newFiltersTariffsSelector,
} from '../redux/selectors/filtersSelector';
import {
    calculateCartTotal,
    calculateProductTotal,
    cartAccountFromQuerySelector,
    cartAccountSelector,
    cartCartSelector,
    cartCurrencyFromQuerySelector,
    cartDomainsFromQuerySelector,
    cartExtraFromQuerySelector,
    cartPeriodIdFromQuerySelector,
    cartTariffIdBeforeAuthSelector,
    cartTariffIdFromQuerySelector,
} from '../redux/selectors/cartSelector';
import { productCurrentProductIdSelector } from '../redux/selectors/productSelector';
import { authCurrencySelector } from '../redux/selectors/authSelectors';
import { useDispatch, useSelector } from 'react-redux';
import {
    includeExtra,
    setCurrency,
    setDomains,
    setNewPeriod,
    setNewTariff,
} from '../redux/actions/cartAction';
import {
    FEATURED_PRODUCTS_PAIR,
    MINIMUM_ALLOWED_ORDER_TOTAL,
} from '../constants/constants';
import getCurrentTariffByProduct from '../resolvers/getCurrentTariffByProduct';
import getSuggestedTariffByProduct from '../resolvers/getSuggestedTariffByProduct';
import { pagesTariffsAllSelector } from '../redux/selectors/pagesSelectors';
import getSuggestedUpgradeTariffByProduct from '../resolvers/getSuggestedUpgradeTariffByProduct';
import isAllowedTariffForUser from '../resolvers/isAllowedTariffForUser';
import { default as _u } from 'underscore';
import getProductWithAppliedAccount from '../mutators/getProductWithAppliedAccount';

export const useFilters = (product, product_id) => {
    const periodsList = useSelector((state) =>
        newFiltersPeriodByTariffSelector(state, { product_id })
    );
    const currenciesList = useSelector((state) => filtersCurrenciesSelector(state));
    const tariffsByPeriod = useSelector((state) =>
        newFiltersTariffsSelector(state, { product_id })
    );
    const tariffsAll = useSelector((state) => pagesTariffsAllSelector(state));
    const mainProductId = useSelector((state) => productCurrentProductIdSelector(state));
    const tariffFromQuery = useSelector((state) => cartTariffIdFromQuerySelector(state));
    const extraFromQuery = useSelector((state) => cartExtraFromQuerySelector(state));
    const periodFromQuery = useSelector((state) => cartPeriodIdFromQuerySelector(state));
    const tariffBeforeAuth = useSelector((state) => cartTariffIdBeforeAuthSelector(state));
    const currencyFromQuery = useSelector((state) => cartCurrencyFromQuerySelector(state));
    const domainsFromQuery = useSelector((state) => cartDomainsFromQuerySelector(state));
    const accountFromQuery = useSelector((state) => cartAccountFromQuerySelector(state));
    const authCurrency = useSelector((state) => authCurrencySelector(state));
    const currentAccount = useSelector((state) => cartAccountSelector(state));
    const cart =  useSelector((state) => cartCartSelector(state));
    const dispatch = useDispatch();

    useEffect(() => {
        if (product?.product_id !== mainProductId || !product?.tariff) {
            return;
        }
        const cartProducts = Object.values(cart?.products)?.filter(item => item?.product_id !== product?.product_id);
        const totalCart = calculateCartTotal({
            ... cart,
            products: cartProducts
        });
        const totalProduct = calculateProductTotal(product, cart, true, tariffsByPeriod);
        const total = (totalCart?.total ?? 0) + totalProduct;
        if (total < MINIMUM_ALLOWED_ORDER_TOTAL) {
            const suggestedTariff = getSuggestedUpgradeTariffByProduct(
                product,
                tariffsByPeriod,
                tariffsAll,
                null,
                cart?.products[mainProductId],
                cart
            );
            if (suggestedTariff && product?.tariff?.tariff_id !== suggestedTariff?.tariff_id) {
                dispatch(setNewTariff({ product: product, tariff: suggestedTariff }));
            }
        }
    }, [product?.tariff, product?.period, product?.includeExtra, cart?.products, product, mainProductId, cart, tariffsByPeriod, tariffsAll, dispatch]);

    useEffect(() => {
        if (product?.product_id !== mainProductId || !cart?.products[mainProductId]?.isMain) {
            return;
        }
        // Filter out only available periods list.
        const applicablePeriodsArray = periodsList
            ? Object.values(periodsList)?.filter(period => !period?.isDisabled)
            : null;
        const applicablePeriodsList = _u.object(applicablePeriodsArray?.map(period => period?.name), applicablePeriodsArray);
        // If there is at least one available period and the tariff is set.
        if (Object.values(applicablePeriodsList).length && !!product?.tariff) {
            const periods = Object.values(applicablePeriodsList)?.sort((a, b) => +b?.interval - +a?.interval);
            // Find the current (latest) set period on product.
            const period = periods?.find((period => {
                return period?.billing_period === product?.period?.billing_period
                    && period?.interval === product?.period?.interval
            }));
            let periodsSuggestedArray = [period];
            // Restore period from query.
            if (periodFromQuery && product_id === mainProductId && !period) {
                periodsSuggestedArray.push(applicablePeriodsList[periodFromQuery]);
            }
            // By default, for DB API - use period = 3 month (setup in defaults).
            const periodSuggested = product?.product_id === 2
                ? periods?.filter((item) => {
                    return item?.interval === FEATURED_PRODUCTS_PAIR[product.product_id]?.defaults?.period_interval
                        && item?.billing_period === FEATURED_PRODUCTS_PAIR[product.product_id]?.defaults?.period
                        && !item?.isDisabled;
                })
                // Otherwise take all active periods.
                : periods?.filter(item => !item?.isDisabled);
            periodsSuggestedArray.push(...periodSuggested);

            // Iterate over periods priority list.
            periodsSuggestedArray.find((periodSuggestedItem) => {
                const cartProducts = Object.values(cart?.products)
                    ?.map(item => {
                        return getProductWithAppliedAccount({ ...item, period: periodSuggestedItem }, cart?.account_id)
                    });
                if (!!periodSuggestedItem && !periodSuggestedItem?.isDisabled) {
                    if (isAllowedTariffForUser({
                        tariff: { ...product?.tariff, periods: [periodSuggestedItem] },
                        product: { ...product, period: periodSuggestedItem },
                        cart: {
                            ...cart,
                            products: _u.object(cartProducts?.map(item => item?.product_id), cartProducts)
                        },
                        mainProduct: { ...cart?.products[mainProductId], period: periodSuggestedItem }
                    })) {
                        if (product?.period?.billing_period !== periodSuggestedItem?.billing_period ||
                            product?.period?.interval !== periodSuggestedItem?.interval) {
                            dispatch(setNewPeriod({ period: periodSuggestedItem, product_id }));
                        }
                        return true;
                    }
                    return false;
                }
            });

        } else if (!periodsList?.length && !!product?.tariff) {
            const suggestedTariff = getSuggestedUpgradeTariffByProduct(
                product,
                tariffsByPeriod,
                tariffsAll,
                null,
                cart?.products[mainProductId],
                cart
            );
            if (suggestedTariff && product?.tariff?.tariff_id !== suggestedTariff?.tariff_id) {
                dispatch(setNewTariff({ product: product, tariff: suggestedTariff }));
            }
        }
    }, [product?.tariff, product?.period, product?.includeExtra, product, mainProductId, cart, periodsList, periodFromQuery, product_id, dispatch, tariffsByPeriod, tariffsAll]);

    useEffect(() => {

        if(!product_id) return;

        const currentTariffId = getCurrentTariffByProduct(product);
        const currentTariff = currentTariffId
            ? tariffsAll[currentTariffId]
            : null;
        const mainProduct = cart?.products[mainProductId];
        const suggestedTariff = getSuggestedTariffByProduct(product, tariffsByPeriod, mainProduct, cart);
        if (tariffsByPeriod) {
            if (
                tariffBeforeAuth &&
                product_id === mainProductId &&
                !tariffsByPeriod[tariffBeforeAuth]?.isDisabled &&
                isAllowedTariffForUser({
                    tariff: tariffsByPeriod[tariffBeforeAuth],
                    product,
                    cart,
                    atLeastOnePeriod: true
                })
            ) {
                dispatch(
                    setNewTariff({
                        tariff: tariffsByPeriod[tariffBeforeAuth],
                        product_id: product_id,
                    })
                );
            } else if (
                tariffFromQuery &&
                product_id === mainProductId &&
                !tariffsByPeriod[tariffFromQuery]?.isDisabled &&
                isAllowedTariffForUser({
                    tariff: tariffsByPeriod[tariffFromQuery],
                    product,
                    cart,
                    atLeastOnePeriod: true
                })
            ) {
                dispatch(
                    setNewTariff({
                        tariff: tariffsByPeriod[tariffFromQuery],
                        product_id: product_id,
                    })
                );
            // For DB API, suggest by default 50 000 checks tariff if the current one has less checks.
            // @see https://doboard.com/1/task/6415#comment_40149
            } else if (currentTariff &&
                currentTariff?.checks <= FEATURED_PRODUCTS_PAIR[product.product_id]?.defaults?.period_interval &&
                suggestedTariff &&
                isAllowedTariffForUser({
                    tariff: suggestedTariff,
                    product,
                    cart,
                    atLeastOnePeriod: true
                })
            ) {
                dispatch(setNewTariff({
                    tariff: suggestedTariff,
                    product_id: product_id,
                }));
            } else if (
                currentTariffId &&
                !tariffsByPeriod[currentTariffId]?.isDisabled &&
                tariffsByPeriod[currentTariffId]?.billing_period ===
                    product?.period?.billing_period &&
                isAllowedTariffForUser({
                    tariff: tariffsByPeriod[currentTariffId],
                    product,
                    cart,
                    atLeastOnePeriod: true
                })
            ) {
                dispatch(
                    setNewTariff({
                        tariff: tariffsByPeriod[currentTariffId],
                        product_id: product_id,
                    })
                );
            } else {
                dispatch(
                    setNewTariff({
                        tariff: Object.values(tariffsByPeriod)
                            ?.filter(
                                (tariff) => !tariff?.isDisabled &&
                                    tariff?.billing_period === 'Year' &&
                                    isAllowedTariffForUser({ tariff, product, cart, atLeastOnePeriod: true })
                            )
                            ?.sort((a, b) => a?.services - b?.services)
                            ?.shift(),
                        product_id: product_id,
                    })
                );
            }
        }
    }, [product_id, currentAccount]);

    useEffect(() => {
        if (Object.values(currenciesList).length) {
            if (currencyFromQuery) {
                dispatch(setCurrency(currenciesList[currencyFromQuery]));
            } else {
                if (authCurrency) {
                    dispatch(setCurrency(currenciesList[authCurrency]));
                } else {
                    dispatch(setCurrency(currenciesList['USD']));
                }
            }
        }
    }, [currenciesList && Object.values(currenciesList).length, currencyFromQuery]);

    useEffect(() => {
        if (domainsFromQuery && domainsFromQuery.length) {
            dispatch(setDomains({
                domains: domainsFromQuery?.map(domain => {
                    return { value: domain }
                })
            }));
        }
    }, [domainsFromQuery, product?.ssl_certs]);

    useEffect(() => {
        dispatch(includeExtra({ value: extraFromQuery }))
    }, [extraFromQuery]);
};
